import { Plan } from "@/app/billing/plan";
import { CreateSubscriptionValues } from "@/app/billing/subscription.schema";
import { FormikProps } from "formik";
import { FC, useMemo } from "react";
import { SubscriptionSummary } from "../molecules/SubscriptionSummary";
import { InputWithCornerHint } from "../atoms/InputWithCornerHint";

import { PaymentMethodSelect } from "./PaymentMethodSelect";
import { PaymentMethod } from "@/app/billing/payment-method";
import { PromotionCode } from "@/app/billing/promotion-code";

export type CreateSubscriptionFormProps = {
  isLoading: boolean;
  plan: Plan;
  formik: FormikProps<CreateSubscriptionValues>;
  paymentMethods: Array<PaymentMethod>;
  promotionCode: PromotionCode;
  isLoadingPromotionCode?: boolean;
};

export const CreateSubscriptionForm: FC<CreateSubscriptionFormProps> = ({
  plan,
  formik,
  paymentMethods,
  promotionCode,
  isLoadingPromotionCode,
}) => {
  const currency = useMemo(() => {
    return plan.price.currency.toUpperCase();
  }, [plan]);

  const showAddOnsSection = useMemo(() => {
    return plan.addOns.length > 0;
  }, [plan]);

  const showDiscountSection = useMemo(() => {
    return !!promotionCode;
  }, [promotionCode]);

  const planTotal = useMemo(() => {
    return (plan.price.amount / 100).toFixed(2);
  }, [plan, promotionCode]);

  const addOnsTotal = useMemo(() => {
    const total = plan.addOns
      .filter((addOn) => formik.values.addOns.includes(addOn.id))
      .reduce((total, addOn) => total + addOn.price.amount / 100, 0);

    return total.toFixed(2);
  }, [plan, formik.values.addOns]);

  const subTotal = useMemo(() => {
    return (parseFloat(planTotal) + parseFloat(addOnsTotal)).toFixed(2);
  }, [planTotal, addOnsTotal]);

  const discountTotal = useMemo(() => {
    if (!promotionCode) return (0).toFixed(2);

    if (promotionCode.coupon.percent) {
      const percentage = promotionCode.coupon.percent / 100;

      console.debug(percentage);

      return (parseFloat(subTotal) * percentage).toFixed(2);
    }
  }, [subTotal, promotionCode]);

  const subTotalWithDiscount = useMemo(() => {
    return (parseFloat(subTotal) - parseFloat(discountTotal)).toFixed(2);
  }, [subTotal, discountTotal]);

  const taxTotal = useMemo(() => {
    if (!plan.price.taxCalculation.percentage) return (0).toFixed(2);

    const percentage = plan.price.taxCalculation.percentage / 100;

    return (parseFloat(subTotalWithDiscount) * percentage).toFixed(2);
  }, [plan, subTotalWithDiscount]);

  const total = useMemo(() => {
    return (parseFloat(subTotalWithDiscount) + parseFloat(taxTotal)).toFixed(2);
  }, [subTotalWithDiscount, taxTotal]);

  return (
    <form action="">
      <div className="mt-6 mx-2">
        <div className="space-y-12">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3 border-b border-gray-200">
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  Datos de la compra
                </h2>
              </div>
            </div>
            <div className="grid max-w-2xl grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-6 md:col-span-2">
              <div className="col-span-full">
                <SubscriptionSummary plan={plan} />
              </div>
              {plan?.addOns.length > 0 && (
                <div className="col-span-full">
                  {plan?.addOns.map((addOn, index) => (
                    <div className="col-span-full">
                      <div className="relative flex items-start">
                        <div className="flex h-6 items-center">
                          <input
                            checked={formik.values.addOns?.includes(addOn.id)}
                            onChange={(event) =>
                              formik.setFieldValue(
                                "addOns",
                                event.target.checked
                                  ? [...formik.values.addOns, addOn.id]
                                  : (formik.values.addOns.filter(
                                      (addOnId) => addOnId != addOn.id
                                    ) ?? [])
                              )
                            }
                            type="checkbox"
                            aria-describedby=""
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-indigo-600"
                          />
                        </div>
                        <div className="ml-3 text-sm leading-6">
                          <label
                            htmlFor=""
                            className="text-gray-900 flex flex-col"
                          >
                            <span>{addOn.name}</span>
                            <small>{addOn.description}</small>
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="col-span-full">
                <InputWithCornerHint
                  label="Cupón"
                  type="text"
                  placeholder="Nombre"
                  name="code"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={!!formik.errors.code}
                  textError={formik.errors.code}
                  value={formik.values.code}
                />
              </div>
              <div className="col-span-full">
                <div className="max-w-md mx-auto rounded-lg">
                  <table className="w-full border-collapse">
                    <tbody className="text-sm">
                      <tr>
                        <td className="py-2 text-gray-600">Suscripción</td>
                        <td className="py-2 text-right font-medium">
                          <div className="flex justify-between">
                            <span>{currency}</span>
                            <span>{planTotal}</span>
                          </div>
                        </td>
                      </tr>
                      {showAddOnsSection && (
                        <tr>
                          <td className="py-2 text-gray-600">
                            Productos adicionales
                          </td>
                          <td className="py-2 text-right font-medium">
                            <div className="flex justify-between">
                              <span>{currency}</span>
                              <span>{addOnsTotal}</span>
                            </div>
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td className="py-2 text-gray-600">Subtotal</td>
                        <td className="py-2 text-right font-medium">
                          <div className="flex justify-between">
                            <span>{currency}</span>
                            <span>{subTotal}</span>
                          </div>
                        </td>
                      </tr>
                      {showDiscountSection && (
                        <tr>
                          <td className="py-2 text-green-600">Descuento</td>
                          <td className="py-2 text-right font-medium">
                            <div className="flex justify-between">
                              <span>{currency}</span>
                              <span>-{discountTotal}</span>
                            </div>
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td className="py-2 text-gray-600">Impuestos</td>
                        <td className="py-2 text-right font-medium">
                          <div className="flex justify-between">
                            <span>{currency}</span>
                            <span>{taxTotal}</span>
                          </div>
                        </td>
                      </tr>
                      <tr className="border-t-2 border-gray-300">
                        <td className="py-2 font-bold">Total</td>
                        <td className="py-2 text-right font-bold">
                          <div className="flex justify-between">
                            <span>{currency}</span>
                            <span>{total}</span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  Método de pago
                </h2>
              </div>
            </div>
            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
              <div className="col-span-full">
                <PaymentMethodSelect
                  paymentMethods={paymentMethods ?? []}
                  onPaymentMethodChange={(paymentMethodId: string) =>
                    formik.setFieldValue("paymentMethodId", paymentMethodId)
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};
